import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, FormControlLabel, Button, MenuItem, Select, InputLabel, FormControl, Slider, Typography, Box, Container, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  fetchStates, 
  fetchDistrictsByState, 
  fetchTalukasByDistrict,
  fetchCastCategories,
  fetchCasts,
  fetchYojanaCategories,
  addScheme,
  createYojanaApproval,
  uploadYojanaVideo
} from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';

const CreateScheme = () => {
    const history = useNavigate();
    const { isYojanaAdmin } = usePermissions();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eligibility: '',
        terms: '',
        benefits: '',
        documents: '',
        ageGroup: false,
        ageRange: [0, 100],
        category: { categoryId: '' },
        gender: {
            male: false,
            female: false,
        },
        state: { stateId: '' },
        district: { districtId: '' },
        taluka: { talukaId: '' },
        incomeGroup: false,
        incomeRange: [0, 1000000],
        government: '',
        department: '',
        castCategory: { categoryId: '' },
        cast: { castId: '' },
        yojanaCategory: { categoryId: '' },
        yojanaVideoUrl: '',
        grLink: ''
    });

    // Master data states
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [talukas, setTalukas] = useState([]);
    const [castCategories, setCastCategories] = useState([]);
    const [casts, setCasts] = useState([]);
    const [schemeCategories, setSchemeCategories] = useState([]);
    const [yojanaCategories, setYojanaCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingTalukas, setLoadingTalukas] = useState(false);
    const [loadingCasts, setLoadingCasts] = useState(false);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [videoFile, setVideoFile] = useState(null);
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoUploadError, setVideoUploadError] = useState(null);
    const [grLinkFile, setGrLinkFile] = useState(null);
    const [uploadedGrLink, setUploadedGrLink] = useState('');
    const [uploadingGrLink, setUploadingGrLink] = useState(false);
    const [grLinkUploadError, setGrLinkUploadError] = useState(null);

    const normalizeYojanaCategory = (category) => {
        const categoryId =
            category?.categoryId ??
            category?.yojanaCategoryId ??
            category?.id;

        const categoryName =
            category?.categoryName ??
            category?.yojanaCategoryName ??
            category?.name ??
            '';

        return {
            ...category,
            categoryId,
            categoryName
        };
    };

    // Fetch master data
    useEffect(() => {
        let cancelled = false;
        const fetchMasterData = async () => {
            try {
                setLoading(true);
                console.log("Fetching master data...");

                const [statesRes, castCategoriesRes, yojanaCategoriesRes, castsRes] = await Promise.all([
                    fetchStates(),
                    fetchCastCategories(),
                    fetchYojanaCategories(),
                    fetchCasts()
                ]);
                if (cancelled) return;

                console.log("States response:", statesRes);
                console.log("Caste Categories response:", castCategoriesRes);
                console.log("Yojana Categories response:", yojanaCategoriesRes);

                if (statesRes.data) setStates(statesRes.data);
                if (castCategoriesRes.data) setCastCategories(castCategoriesRes.data);
                if (castsRes?.data) setCasts(castsRes.data);
                if (yojanaCategoriesRes.data) {
                    const normalizedCategories = Array.isArray(yojanaCategoriesRes.data)
                        ? yojanaCategoriesRes.data.map(normalizeYojanaCategory)
                        : [];
                    setSchemeCategories(normalizedCategories);
                    setYojanaCategories(normalizedCategories);
                }

                setError(null);
            } catch (error) {
                console.error("Error fetching master data:", error);
                console.error("Error details:", error.response?.data);
                if (!cancelled) {
                    setError(error.response?.data?.message || "Failed to load master data. Please try again later.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchMasterData();
        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch districts when state changes
    useEffect(() => {
        let cancelled = false;
        const fetchDistrictData = async () => {
            if (formData.state.stateId) {
                try {
                    setLoadingDistricts(true);
                    const response = await fetchDistrictsByState(formData.state.stateId);
                    if (cancelled) return;
                    setDistricts(response.data);
                    setError(null);
                } catch (error) {
                    console.error("Error fetching districts:", error);
                    if (!cancelled) setError("Failed to load districts. Please try again.");
                } finally {
                    if (!cancelled) setLoadingDistricts(false);
                }
            }
        };

        fetchDistrictData();
        return () => {
            cancelled = true;
        };
    }, [formData.state.stateId]);

    // Fetch talukas when district changes
    useEffect(() => {
        let cancelled = false;
        const fetchTalukaData = async () => {
            if (formData.district.districtId) {
                try {
                    setLoadingTalukas(true);
                    const response = await fetchTalukasByDistrict(formData.district.districtId);
                    if (cancelled) return;
                    setTalukas(response.data);
                    setError(null);
                } catch (error) {
                    console.error("Error fetching talukas:", error);
                    if (!cancelled) setError("Failed to load talukas. Please try again.");
                } finally {
                    if (!cancelled) setLoadingTalukas(false);
                }
            }
        };

        fetchTalukaData();
        return () => {
            cancelled = true;
        };
    }, [formData.district.districtId]);

    const handleChange = async (event) => {
        const { name, value } = event.target;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            
            // Handle state change
            if (name === 'state.stateId') {
                setFormData(prev => ({
                    ...prev,
                    state: { stateId: value },
                    district: { districtId: '' },
                    taluka: { talukaId: '' }
                }));
                
                setDistricts([]);
                setTalukas([]);
                
                if (value) {
                    setLoadingDistricts(true);
                    try {
                        const response = await fetchDistrictsByState(value);
                        if (response.data) {
                            setDistricts(response.data);
                        } else {
                            setError('No districts found for selected state');
                        }
                    } catch (error) {
                        console.error('Error fetching districts:', error);
                        setError('Failed to load districts');
                    } finally {
                        setLoadingDistricts(false);
                    }
                }
            }
            // Handle district change
            else if (name === 'district.districtId') {
                setFormData(prev => ({
                    ...prev,
                    district: { districtId: value },
                    taluka: { talukaId: '' }
                }));
                
                setTalukas([]);
                
                if (value) {
                    setLoadingTalukas(true);
                    try {
                        const response = await fetchTalukasByDistrict(value);
                        if (response.data) {
                            setTalukas(response.data);
                        } else {
                            setError('No talukas found for selected district');
                        }
                    } catch (error) {
                        console.error('Error fetching talukas:', error);
                        setError('Failed to load talukas');
                    } finally {
                        setLoadingTalukas(false);
                    }
                }
            }
            else if (name === 'castCategory.categoryId') {
                setFormData(prev => ({
                    ...prev,
                    castCategory: { categoryId: value },
                    cast: { castId: '' }
                }));
            }
            // Handle category change
            else if (name === 'category.categoryId') {
                setFormData(prev => ({
                    ...prev,
                    category: { categoryId: value }
                }));
            }
            else if (name === 'yojanaCategory.categoryId') {
                setFormData(prev => ({
                    ...prev,
                    yojanaCategory: { categoryId: value }
                }));
            }
            // Handle other nested changes
            else {
                setFormData(prev => ({
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: value
                    }
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleVideoChange = (event) => {
        const file = event.currentTarget.files && event.currentTarget.files[0]
            ? event.currentTarget.files[0]
            : null;
        setVideoFile(file);
        setVideoUploadError(null);
        if (!file) {
            setUploadedVideoUrl('');
            setFormData(prev => ({
                ...prev,
                yojanaVideoUrl: ''
            }));
        }
    };

    const handleGrLinkChange = (event) => {
        const file = event.currentTarget.files && event.currentTarget.files[0]
            ? event.currentTarget.files[0]
            : null;
        setGrLinkFile(file);
        setGrLinkUploadError(null);
        if (!file) {
            setUploadedGrLink('');
            setFormData(prev => ({
                ...prev,
                grLink: ''
            }));
        }
    };

    const handleGrLinkDelete = () => {
        setGrLinkFile(null);
        setUploadedGrLink('');
        setGrLinkUploadError(null);
        setFormData(prev => ({
            ...prev,
            grLink: ''
        }));
    };

    const handleSliderChange = (name) => (event, newValue) => {
        setFormData(prev => ({
            ...prev,
            [name]: newValue,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setLoading(true);
            setVideoUploadError(null);
            setGrLinkUploadError(null);
            let payload = { ...formData };

            if (videoFile) {
                setUploadingVideo(true);
                try {
                    const uploadResponse = await uploadYojanaVideo(videoFile);
                    const responseData = uploadResponse?.data;
                    const videoUrl = typeof responseData === 'string'
                        ? responseData
                        : responseData?.yojanaVideoUrl || responseData?.url || responseData?.location || '';

                    if (!videoUrl) {
                        throw new Error('Video upload did not return a valid URL.');
                    }

                    payload = {
                        ...payload,
                        yojanaVideoUrl: videoUrl
                    };
                    setUploadedVideoUrl(videoUrl);
                    setFormData(prev => ({
                        ...prev,
                        yojanaVideoUrl: videoUrl
                    }));
                } catch (uploadError) {
                    console.error('Error uploading yojana video:', uploadError);
                    const message = uploadError.response?.data?.message || uploadError.message || 'Failed to upload video. Please try again.';
                    setVideoUploadError(message);
                    setError(message);
                    setLoading(false);
                    return;
                } finally {
                    setUploadingVideo(false);
                }
            }

            if (grLinkFile) {
                setUploadingGrLink(true);
                try {
                    const uploadResponse = await uploadYojanaVideo(grLinkFile);
                    const responseData = uploadResponse?.data;
                    const grUrl = typeof responseData === 'string'
                        ? responseData
                        : responseData?.grLink || responseData?.yojanaVideoUrl || responseData?.url || responseData?.location || '';

                    if (!grUrl) {
                        throw new Error('GR link file upload did not return a valid URL.');
                    }

                    payload = {
                        ...payload,
                        grLink: grUrl
                    };
                    setUploadedGrLink(grUrl);
                    setFormData(prev => ({
                        ...prev,
                        grLink: grUrl
                    }));
                } catch (uploadError) {
                    console.error('Error uploading GR link file:', uploadError);
                    const message = uploadError.response?.data?.message || uploadError.message || 'Failed to upload GR link file. Please try again.';
                    setGrLinkUploadError(message);
                    setError(message);
                    setLoading(false);
                    return;
                } finally {
                    setUploadingGrLink(false);
                }
            }

            if (!payload.category?.categoryId) {
                delete payload.category;
            }

            if (!payload.yojanaCategory?.categoryId) {
                delete payload.yojanaCategory;
            }

            if (isYojanaAdmin()) {
                await addScheme(payload, {
                    identifier: payload.title || payload.schemeName
                });
            } else {
                await createYojanaApproval(payload);
            }
            setVideoFile(null);
            setGrLinkFile(null);
            history.push('/dashboard/schemes');
        } catch (error) {
            console.error("Error creating scheme:", error);
            setError(error.response?.data?.message || "Failed to create scheme. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                <Typography variant="h4" gutterBottom>Create Yojana</Typography>
                {error && <Typography color="error" gutterBottom>{error}</Typography>}
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={4}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Eligibility"
                                name="eligibility"
                                value={formData.eligibility}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Terms & Conditions"
                                name="terms"
                                value={formData.terms}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Benefits"
                                name="benefits"
                                value={formData.benefits}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Yojana Documents"
                                name="documents"
                                value={formData.documents}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.ageGroup} onChange={handleChange} name="ageGroup" />}
                                label="Age Group"
                            />
                        </Grid>
                        {formData.ageGroup && (
                            <Grid item xs={12}>
                                <Typography>Age Range {formData.ageRange[0]} to {formData.ageRange[1]}</Typography>
                                <Slider
                                    value={formData.ageRange}
                                    onChange={handleSliderChange('ageRange')}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={100}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>State</InputLabel>
                                <Select
                                    name="state.stateId"
                                    value={formData.state.stateId}
                                    onChange={handleChange}
                                    required
                                >
                                    {states.map((state) => (
                                        <MenuItem key={state.stateId} value={state.stateId}>
                                            {state.stateName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>District</InputLabel>
                                <Select
                                    name="district.districtId"
                                    value={formData.district.districtId}
                                    onChange={handleChange}
                                    required
                                    disabled={!formData.state.stateId || loadingDistricts}
                                >
                                    {loadingDistricts ? (
                                        <MenuItem disabled>Loading districts...</MenuItem>
                                    ) : districts.length === 0 ? (
                                        <MenuItem disabled>No districts available</MenuItem>
                                    ) : (
                                        districts.map((district) => (
                                            <MenuItem key={district.districtId} value={district.districtId}>
                                                {district.districtName}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Taluka</InputLabel>
                                <Select
                                    name="taluka.talukaId"
                                    value={formData.taluka.talukaId}
                                    onChange={handleChange}
                                    required
                                    disabled={!formData.district.districtId || loadingTalukas}
                                >
                                    {loadingTalukas ? (
                                        <MenuItem disabled>Loading talukas...</MenuItem>
                                    ) : talukas.length === 0 ? (
                                        <MenuItem disabled>No talukas available</MenuItem>
                                    ) : (
                                        talukas.map((taluka) => (
                                            <MenuItem key={taluka.talukaId} value={taluka.talukaId}>
                                                {taluka.talukaName}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Caste Category</InputLabel>
                                <Select
                                    name="castCategory.categoryId"
                                    value={formData.castCategory.categoryId}
                                    onChange={handleChange}
                                    label="Caste Category"
                                    required
                                >
                                    <MenuItem value="">Select Category</MenuItem>
                                    {castCategories.map((category) => (
                                        <MenuItem key={category.categoryId} value={category.categoryId}>
                                            {category.categoryName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category.categoryId"
                                    value={formData.category.categoryId}
                                    onChange={handleChange}
                                    label="Category"
                                    required
                                >
                                    <MenuItem value="">Select Category</MenuItem>
                                    {schemeCategories.map((category) => (
                                        <MenuItem key={category.categoryId} value={category.categoryId}>
                                            {category.categoryName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Yojana Category</InputLabel>
                                <Select
                                    name="yojanaCategory.categoryId"
                                    value={formData.yojanaCategory.categoryId}
                                    onChange={handleChange}
                                    label="Yojana Category"
                                    required
                                >
                                    <MenuItem value="">Select Yojana Category</MenuItem>
                                    {yojanaCategories.map((category) => (
                                        <MenuItem key={category.categoryId} value={category.categoryId}>
                                            {category.categoryName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Caste</InputLabel>
                                <Select
                                    name="cast.castId"
                                    value={formData.cast.castId}
                                    onChange={handleChange}
                                    label="Caste"
                                    required
                                    disabled={loadingCasts}
                                >
                                    <MenuItem value="">Select Caste</MenuItem>
                                    {loadingCasts ? (
                                        <MenuItem disabled>Loading castes...</MenuItem>
                                    ) : (
                                        casts.map((cast) => (
                                            <MenuItem key={cast.castId} value={cast.castId}>
                                                {cast.castName}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <Button
                                    variant="outlined"
                                    component="label"
                                >
                                    {videoFile ? 'Change Yojana Video' : 'Upload Yojana Video'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="video/*"
                                        onChange={handleVideoChange}
                                    />
                                </Button>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {videoFile
                                        ? `Selected file: ${videoFile.name}`
                                        : uploadedVideoUrl
                                            ? `Uploaded video URL: ${uploadedVideoUrl}`
                                            : 'No video selected. This field is optional.'}
                                </Typography>
                                {videoUploadError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        {videoUploadError}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                    >
                                        {grLinkFile ? 'Change GR Link File' : 'Upload GR Link'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="*/*"
                                            onChange={handleGrLinkChange}
                                        />
                                    </Button>
                                    {(grLinkFile || formData.grLink) && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleGrLinkDelete}
                                        >
                                            Remove GR Link
                                        </Button>
                                    )}
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {grLinkFile
                                        ? `Selected file: ${grLinkFile.name}`
                                        : formData.grLink
                                            ? `Uploaded GR link: ${formData.grLink}`
                                            : 'No GR link file selected. This field is optional.'}
                                </Typography>
                                {grLinkUploadError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        {grLinkUploadError}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.gender.male} onChange={handleChange} name="gender.male" />}
                                label="Male"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={formData.gender.female} onChange={handleChange} name="gender.female" />}
                                label="Female"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.incomeGroup} onChange={handleChange} name="incomeGroup" />}
                                label="Income Group"
                            />
                        </Grid>
                        {formData.incomeGroup && (
                            <Grid item xs={12}>
                                <Typography>Income Range (in Rupees)</Typography>
                                <Slider
                                    value={formData.incomeRange}
                                    onChange={handleSliderChange('incomeRange')}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={1000000}
                                    step={10000}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Yojana Government</InputLabel>
                                <Select
                                    name="government"
                                    value={formData.government}
                                    onChange={handleChange}
                                    required
                                >
                                    <MenuItem value="central">Central</MenuItem>
                                    <MenuItem value="state">State</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Yojana Department</InputLabel>
                                <Select
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    required
                                >
                                    <MenuItem value="health">Health</MenuItem>
                                    <MenuItem value="education">Education</MenuItem>
                                    <MenuItem value="agriculture">Agriculture</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                fullWidth
                                disabled={loading || uploadingVideo || uploadingGrLink}
                            >
                                {loading ? (uploadingVideo ? "Uploading video..." : uploadingGrLink ? "Uploading GR link..." : "Creating...") : "Create Scheme"}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Container>
    );
};

export default CreateScheme;